import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import clsx from 'clsx'

import { AvatarImage } from 'components/common'
import type { PhoneAccount } from 'services/auth'

export type PhoneLoginMethod = 'sms' | 'password'

/** Name first (like the account's profile), masked email underneath; either may be missing. */
function AccountIdentity({ account }: Readonly<{ account: PhoneAccount }>) {
	const title = account.name || account.maskedEmail || 'Loyalink account'
	const caption = account.name && account.maskedEmail ? account.maskedEmail : 'Loyalink account'

	return (
		<div className="flex min-w-0 items-center gap-4">
			<AvatarImage src={account.avatarUrl} alt="" className="size-14 shrink-0" />
			<div className="min-w-0 text-left">
				<p className="truncate font-semibold text-white">{title}</p>
				<p className="truncate text-sm text-secondary-text">{caption}</p>
			</div>
		</div>
	)
}

/** The account being signed in to, shown on the steps that follow the choice so it stays clear which one it is. */
export function PhoneAccountCard({ account }: Readonly<{ account: PhoneAccount }>) {
	return (
		<div
			className="rounded-xl border border-translucent-border bg-primary-surface/[0.4] p-4"
			data-testid="phone-account-card"
		>
			<AccountIdentity account={account} />
		</div>
	)
}

interface PhoneAccountChooserProps {
	readonly accounts: PhoneAccount[]
	readonly onSelect: (account: PhoneAccount) => void
}

/** "Choose your account": only shown when the phone matches more than one account. */
export function PhoneAccountChooser({ accounts, onSelect }: PhoneAccountChooserProps) {
	return (
		<div className="space-y-3" data-testid="phone-account-chooser">
			<p className="text-sm text-secondary-text">These accounts match the phone number you entered.</p>
			{accounts.map(account => (
				<button
					key={account.handle}
					type="button"
					onClick={() => onSelect(account)}
					className="flex w-full items-center justify-between gap-3 rounded-xl border border-translucent-border bg-primary-surface/[0.4] p-4 transition-colors hover:bg-secondary-surface/[0.6]"
				>
					<AccountIdentity account={account} />
					<FontAwesomeIcon
						icon={['fas', 'chevron-right']}
						className="text-secondary-text"
						aria-hidden="true"
					/>
				</button>
			))}
		</div>
	)
}

interface MethodOptionProps {
	readonly value: PhoneLoginMethod
	readonly selected: boolean
	readonly title: string
	readonly detail: string
	readonly onChange: (method: PhoneLoginMethod) => void
}

function MethodOption({ value, selected, title, detail, onChange }: MethodOptionProps) {
	const id = `phone-login-method-${value}`
	return (
		<label htmlFor={id} className="flex cursor-pointer items-center justify-between gap-3 px-4 py-3">
			<span className="min-w-0">
				<span className="block font-medium text-white">{title}</span>
				<span className="block text-sm text-secondary-text">{detail}</span>
			</span>
			<input
				id={id}
				type="radio"
				name="phone-login-method"
				value={value}
				checked={selected}
				aria-label={`${title}: ${detail}`}
				onChange={() => onChange(value)}
				className="size-5 shrink-0 accent-primary"
			/>
		</label>
	)
}

interface PhoneLoginMethodPickerProps {
	readonly account: PhoneAccount
	readonly phoneNumber: string
	readonly method: PhoneLoginMethod
	/** Starts the emailed-code sign-in (the account's email is only known masked, so the user types it) */
	readonly onForgotPassword: () => void
	/** True while a request is in flight: switching flows then would be overwritten by its reply */
	readonly forgotDisabled?: boolean
	readonly onMethodChange: (method: PhoneLoginMethod) => void
}

/** "Choose a way to log in": the matched account, then SMS code or password. */
export function PhoneLoginMethodPicker({
	account,
	phoneNumber,
	method,
	onForgotPassword,
	forgotDisabled = false,
	onMethodChange,
}: Readonly<PhoneLoginMethodPickerProps>) {
	return (
		<div className="space-y-4" data-testid="phone-login-method-picker">
			<p className="text-center text-sm text-secondary-text">
				You already have an account with this phone number.
			</p>
			<PhoneAccountCard account={account} />
			<fieldset
				className={clsx(
					// min-w-0: a fieldset defaults to min-width: min-content and would overflow on narrow screens
					'min-w-0 divide-y divide-translucent-border overflow-hidden rounded-xl border border-translucent-border',
					'bg-primary-surface/[0.4]'
				)}
			>
				<legend className="sr-only">Choose a way to log in</legend>
				<MethodOption
					value="sms"
					selected={method === 'sms'}
					title="Get code via SMS"
					detail={phoneNumber}
					onChange={onMethodChange}
				/>
				<MethodOption
					value="password"
					selected={method === 'password'}
					title="Continue with password"
					detail="Use your password to continue"
					onChange={onMethodChange}
				/>
			</fieldset>
			<p className="text-center text-sm">
				<button
					type="button"
					className="-my-2.5 inline-block py-2.5 text-link hover:text-link-hover disabled:opacity-50"
					disabled={forgotDisabled}
					onClick={onForgotPassword}
				>
					Forgot your password? Sign in with an emailed code
				</button>
			</p>
		</div>
	)
}
